import { useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Pencil, Plus, Trash2 } from 'lucide-react'

import { api } from '@/api/client'
import { confirm } from '@/stores/confirm'
import type { LabelItemMap, PurchasePartnerHit, Supplier } from '@/types/models'
import { useToast } from '@/stores/toast'
import { Modal } from '@/components/Modal'
import { ItemPicker } from '@/components/ItemPicker'
import { PurchasePartnerCombo } from '@/components/PurchasePartnerCombo'
import { type BulkResult, PurchasePartnerMultiCombo, postToPartners } from '@/components/PurchasePartnerMultiCombo'
import { Field, inputCls } from '@/components/Field'
import { cn } from '@/lib/utils'
import { useDebouncedValue, useRenderLimit } from '@/lib/useListPerf'
import { ShowMoreRow } from '@/components/ShowMoreRow'

export interface PurchaseMapConfig {
  apiBase: string       // '/api/purchase/item-maps' | '/api/purchase/label-maps'
  queryKey: string      // 'purchase-item-maps' | 'label-item-maps'
  showLabelUnit: boolean
  title: string
  allowCommon: boolean  // 거래처 비우면 전 거래처 공통 규칙
  showSupplier?: boolean // 협력업체(품목 단위) 함께 지정 — 구매장만
}

// blank = 구매장용 품목 미입력(새 품목 자동 행 — 품명·특이사항 직접 입력), nosupplier = 협력업체 미지정
type Scope = '' | 'common' | 'partner' | 'blank' | 'nosupplier'

interface MapForm {
  id?: number
  partner: number | null
  partner_label: string
  item: number | null
  item_label: string
  purchase_item: string
  note: string
  label_pack_unit: string
  supplier: number | null       // 품목의 협력업체(비우면 기존 지정 유지)
  partners: PurchasePartnerHit[] // 추가: 여러 거래처 한 번에(비우면 공통) — 수정은 partner 한 곳
}

const blank: MapForm = {
  partner: null, partner_label: '', item: null, item_label: '',
  purchase_item: '', note: '', label_pack_unit: '', supplier: null, partners: [],
}

const supLabel = (s: Pick<Supplier, 'name' | 'kind'>) => (s.kind === 'area' ? `구역 ${s.name}` : s.name)

export function PurchaseMapManage({ config: c }: { config: PurchaseMapConfig }) {
  const qc = useQueryClient()
  const push = useToast((s) => s.push)
  const [search, setSearch] = useState('')
  const [scope, setScope] = useState<Scope>('')
  const [edit, setEdit] = useState<MapForm | null>(null)

  // 검색어는 입력이 멈춘 뒤 보내고(글자마다 요청 X), 새 결과가 올 때까지 이전 목록 유지(깜빡임 X).
  const q = useDebouncedValue(search.trim())
  const { data: rows = [], isFetching } = useQuery({
    queryKey: [c.queryKey, q, scope],
    placeholderData: keepPreviousData,
    queryFn: () => {
      const qs = new URLSearchParams()
      if (q) qs.set('q', q)
      if (scope) qs.set('scope', scope)
      const s = qs.toString()
      return api<LabelItemMap[]>(`${c.apiBase}${s ? `?${s}` : ''}`)
    },
  })

  const { data: suppliers = [] } = useQuery({
    queryKey: ['purchase-suppliers'],
    queryFn: () => api<Supplier[]>('/api/purchase/suppliers'),
    enabled: !!c.showSupplier,
  })

  const remove = useMutation({
    mutationFn: (id: number) => api(`${c.apiBase}/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [c.queryKey] }); push('삭제됨', 'success') },
    onError: (e) => push(`삭제 실패: ${e.message}`, 'error', 5000),
  })

  const save = useMutation({
    mutationFn: (f: MapForm) => {
      const body: Record<string, unknown> = {
        partner: f.partner, item: f.item,
        purchase_item: f.purchase_item.trim(), note: f.note.trim(),
      }
      if (c.showLabelUnit) body.label_pack_unit = f.label_pack_unit.trim()
      if (c.showSupplier) body.supplier = f.supplier
      if (f.id) return api(`${c.apiBase}/${f.id}`, { method: 'PATCH', body })
      if (f.partners.length) {
        const { partner: _p, ...rest } = body
        return postToPartners(c.apiBase, f.partners.map((p) => p.id), rest)
      }
      return api(`${c.apiBase}`, { method: 'POST', body })
    },
    onSuccess: (res) => {
      if (res === null) return              // 덮어쓰기 확인에서 취소 — 창 유지
      // 협력업체는 협력업체 탭·발주서·과일 구매장과 같은 데이터
      for (const k of [c.queryKey, 'purchase-suppliers', 'purchase-item-suppliers', 'supplier-orders', 'purchase-register-all']) {
        qc.invalidateQueries({ queryKey: [k] })
      }
      setEdit(null)
      const b = res as BulkResult | undefined
      push(b && 'created' in b ? `저장됨 — 새로 ${b.created}곳${b.updated ? `, 덮어쓰기 ${b.updated}곳` : ''}` : '저장됨', 'success')
    },
    onError: (e) => push(`저장 실패: ${e.message}`, 'error', 6000),
  })

  function openEdit(r: LabelItemMap) {
    setEdit({
      id: r.id, partner: r.partner,
      partner_label: r.partner_name ?? '',
      item: r.item,
      item_label: `${r.item_code} ${r.item_name}${r.item_unit ? ` (${r.item_unit})` : ''}`,
      purchase_item: r.purchase_item, note: r.note ?? '',
      label_pack_unit: r.label_pack_unit ?? '',
      supplier: r.supplier_id ?? null, partners: [],
    })
  }

  const list = useRenderLimit(rows, `${q}|${scope}`)
  const hasPartner = !!edit && (edit.id ? !!edit.partner : edit.partners.length > 0)
  const canSubmit = !!edit && (c.allowCommon || hasPartner) && !!edit.item && !!edit.purchase_item.trim()
  const colCount = 6 + (c.showLabelUnit ? 1 : 0) + (c.showSupplier ? 1 : 0)

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm mb-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="거래처 / 품목 / 구매장용 품목 검색"
               className={inputCls + ' flex-1 min-w-[220px]'}/>
        <select value={scope} onChange={(e) => setScope(e.target.value as Scope)}
                className={inputCls + ' w-36'}>
          <option value="">전체</option>
          <option value="blank">미입력(새 품목)</option>
          {c.allowCommon && <option value="common">공통만</option>}
          {c.allowCommon && <option value="partner">거래처별만</option>}
          {c.showSupplier && <option value="nosupplier">협력업체 미지정</option>}
        </select>
        <span className="text-xs text-slate-400 tabular-nums">{rows.length}건</span>
        <button onClick={() => setEdit({ ...blank })}
                className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg px-3 py-1.5 inline-flex items-center gap-1">
          <Plus className="w-3.5 h-3.5"/> 매핑 추가
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500 tracking-wide">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium w-56">거래처</th>
              <th className="px-3 py-2.5 text-left font-medium w-52">품목</th>
              <th className="px-3 py-2.5 text-center font-medium w-6"></th>
              <th className="px-3 py-2.5 text-left font-medium">구매장용 품목</th>
              <th className="px-3 py-2.5 text-left font-medium w-48">특이사항</th>
              {c.showLabelUnit && <th className="px-3 py-2.5 text-left font-medium w-32">라벨 포장단위</th>}
              {c.showSupplier && <th className="px-3 py-2.5 text-left font-medium w-28">협력업체</th>}
              <th className="px-3 py-2.5 text-right font-medium w-20"></th>
            </tr>
          </thead>
          <tbody>
            {list.shown.map((r) => (
              <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-1.5 text-slate-800">
                  {r.partner_name ?? (
                    <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">공통</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-slate-700">
                  <span className="font-mono text-xs text-slate-400 mr-1">{r.item_code}</span>{r.item_name}
                  {r.item_unit && <span className="text-xs text-slate-400 ml-1">({r.item_unit})</span>}
                </td>
                <td className="px-3 py-1.5 text-center text-slate-300"><ArrowRight className="w-3.5 h-3.5 inline"/></td>
                <td className="px-3 py-1.5 font-medium text-slate-900">
                  {r.purchase_item || (
                    <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">미입력</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-slate-600 text-xs">{r.note}</td>
                {c.showLabelUnit && <td className="px-3 py-1.5 text-slate-600 text-xs">{r.label_pack_unit}</td>}
                {c.showSupplier && (
                  <td className="px-3 py-1.5 text-xs">
                    {r.supplier_id
                      ? supLabel(suppliers.find((s) => s.id === r.supplier_id) ?? { name: r.supplier_name ?? '', kind: 'supplier' })
                      : <span className="text-slate-400">미지정</span>}
                  </td>
                )}
                <td className="px-3 py-1.5 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(r)} className="text-blue-600 hover:text-blue-800 mr-2"><Pencil className="w-3.5 h-3.5"/></button>
                  <button onClick={() => { void confirm(`'${r.purchase_item || r.item_name}' 매핑 삭제?`, { danger: true }).then((ok) => ok && remove.mutate(r.id)) }}
                          className="text-rose-500 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5"/></button>
                </td>
              </tr>
            ))}
            <ShowMoreRow hidden={list.hidden} colSpan={colCount} onMore={list.more} onAll={list.all}/>
            {!rows.length && !isFetching && (
              <tr><td colSpan={colCount} className="text-center py-10 text-sm text-slate-400">
                매핑이 없습니다. 우측 상단 "매핑 추가"로 시작하세요.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={!!edit} onClose={() => setEdit(null)}
             title={edit?.id ? `${c.title} 매핑 수정` : `${c.title} 매핑 추가`} size="lg">
        {edit && (
          <form onSubmit={(e) => { e.preventDefault(); if (canSubmit) save.mutate(edit) }} className="space-y-3">
            <Field label={(c.allowCommon ? '거래처 (비우면 전 거래처 공통)' : '거래처 * (전 회사)')
                          + (edit.id ? '' : ' — 여러 곳 선택 가능')} full>
              {!edit.id ? (
                <PurchasePartnerMultiCombo value={edit.partners}
                  placeholder={c.allowCommon ? '공통 (거래처 검색해서 지정)' : undefined}
                  onChange={(v) => setEdit({ ...edit, partners: v })}/>
              ) : (
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <PurchasePartnerCombo label={edit.partner_label}
                    placeholder={c.allowCommon ? '공통 (거래처 검색해서 지정)' : undefined}
                    onChange={(p) => setEdit({ ...edit, partner: p.id, partner_label: p.name })}/>
                </div>
                {c.allowCommon && edit.partner && (
                  <button type="button" onClick={() => setEdit({ ...edit, partner: null, partner_label: '' })}
                          className="text-xs text-slate-500 hover:text-slate-800 border border-slate-300 rounded-lg px-2 py-1.5 whitespace-nowrap">
                    공통으로
                  </button>
                )}
              </div>
              )}
            </Field>
            <Field label="품목 *" full>
              <ItemPicker value={edit.item} initialLabel={edit.item_label}
                onChange={(it) => setEdit({ ...edit, item: it.id, item_label: `${it.code} ${it.name}${it.unit_out ? ` (${it.unit_out})` : ''}` })}/>
            </Field>
            <Field label="구매장용 품목 *" full>
              <input value={edit.purchase_item} onChange={(e) => setEdit({ ...edit, purchase_item: e.target.value })}
                     placeholder="구매장에 묶어 표시할 품목명" className={inputCls} autoFocus={!!edit.id && !edit.purchase_item}/>
            </Field>
            <Field label="특이사항" full>
              <input value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} className={inputCls}/>
            </Field>
            {c.showSupplier && (
              <Field label="협력업체 (품목 전체에 적용)" full>
                <select value={edit.supplier ?? ''} className={inputCls}
                        onChange={(e) => setEdit({ ...edit, supplier: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">{edit.supplier ? '' : '지정 안 함 (기존 지정 유지)'}</option>
                  {suppliers.filter((s) => s.is_active || s.id === edit.supplier).map((s) => (
                    <option key={s.id} value={s.id}>{supLabel(s)}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">지정 해제는 협력업체 탭에서 합니다.</p>
              </Field>
            )}
            {c.showLabelUnit && (
              <Field label="라벨 포장단위" full>
                <input value={edit.label_pack_unit} onChange={(e) => setEdit({ ...edit, label_pack_unit: e.target.value })}
                       placeholder="예: 1kg, 500g×10" className={inputCls}/>
              </Field>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEdit(null)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">취소</button>
              <button type="submit" disabled={!canSubmit || save.isPending}
                      className={cn('px-4 py-2 text-sm font-semibold rounded-lg',
                        (!canSubmit || save.isPending) ? 'bg-slate-200 text-slate-400 cursor-not-allowed' : 'bg-emerald-600 hover:bg-emerald-700 text-white')}>
                {save.isPending ? '저장 중...' : '저장'}
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  )
}
